import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AuthEmpleadoContext } from "../../../context/AuthEmpleadoContext";
import AppContext from "../../../AppContext";

function CambiarPassword() {
  const { empleado } = useContext(AuthEmpleadoContext);
  const navigate = useNavigate();
  const mailAutenticado = (
    empleado?.mail ||
    empleado?.email ||
    empleado?.correo ||
    ""
  )
    .trim()
    .toLowerCase();

  const [form, setForm] = useState({
    claveActual: "",
    nuevaClave: "",
    repetirNuevaClave: "",
  });

  const [errores, setErrores] = useState({});
  const [mensaje, setMensaje] = useState("");
  const [tipoMensaje, setTipoMensaje] = useState("");
  const [loading, setLoading] = useState(false);

  const obtenerMensaje = (data, fallback) => {
    if (typeof data === "string" && data.trim()) {
      return data;
    }

    if (typeof data?.mensaje === "string" && data.mensaje.trim()) {
      return data.mensaje;
    }

    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }

    if (typeof data?.error === "string" && data.error.trim()) {
      return data.error;
    }

    if (typeof data?.status === "number") {
      const detalle = [data.error, data.path].filter(Boolean).join(" - ");
      return detalle ? `Error ${data.status}: ${detalle}` : fallback;
    }

    return fallback;
  };

  useEffect(() => {
    if (!empleado) {
      navigate("/loginEmpleados");
    }
  }, [empleado, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validar = () => {
    const nuevosErrores = {};

    if (!mailAutenticado) {
      nuevosErrores.mail = "El email es obligatorio.";
    }

    if (!form.claveActual.trim()) {
      nuevosErrores.claveActual = "La contraseña actual es obligatoria.";
    }

    if (!form.nuevaClave.trim()) {
      nuevosErrores.nuevaClave = "La nueva contraseña es obligatoria.";
    } else if (form.nuevaClave.trim().length < 6) {
      nuevosErrores.nuevaClave =
        "La nueva contraseña debe tener al menos 6 caracteres.";
    }

    if (!form.repetirNuevaClave.trim()) {
      nuevosErrores.repetirNuevaClave =
        "Debés repetir la nueva contraseña.";
    } else if (form.nuevaClave !== form.repetirNuevaClave) {
      nuevosErrores.repetirNuevaClave = "Las contraseñas no coinciden.";
    }

    if (
      form.claveActual.trim() &&
      form.nuevaClave.trim() &&
      form.claveActual.trim() === form.nuevaClave.trim()
    ) {
      nuevosErrores.nuevaClave =
        "La nueva contraseña no puede ser igual a la actual.";
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje("");
    setTipoMensaje("");

    if (!validar()) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${AppContext.baseApiUrl}${AppContext.urlServiceChangePassword}`,
        {
          mail: mailAutenticado,
          clave: form.claveActual.trim(),
          nuevaClave: form.nuevaClave.trim(),
        }
      );

      setMensaje(
        obtenerMensaje(response.data, "Contraseña cambiada correctamente.")
      );
      setTipoMensaje("success");

      setForm((prev) => ({
        ...prev,
        claveActual: "",
        nuevaClave: "",
        repetirNuevaClave: "",
      }));

      navigate("/dashboardEmpleados", {
        replace: true,
        state: {
          mensajeExito: "Contraseña cambiada con éxito.",
        },
      });
    } catch (error) {
      setMensaje(
        obtenerMensaje(
          error?.response?.data,
          "No se pudo cambiar la contraseña."
        )
      );
      setTipoMensaje("danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow rounded-4 border-0">
            <Card.Body className="p-4">
              <h2 className="text-center mb-4">Cambiar contraseña</h2>

              {mensaje && <Alert variant={tipoMensaje}>{mensaje}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={mailAutenticado}
                    readOnly
                    autoComplete="username"
                    isInvalid={!!errores.mail}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errores.mail}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Contraseña actual</Form.Label>
                  <Form.Control
                    type="password"
                    name="claveActual"
                    value={form.claveActual}
                    onChange={handleChange}
                    autoComplete="current-password"
                    isInvalid={!!errores.claveActual}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errores.claveActual}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Nueva contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    name="nuevaClave"
                    value={form.nuevaClave}
                    onChange={handleChange}
                    autoComplete="new-password"
                    isInvalid={!!errores.nuevaClave}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errores.nuevaClave}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Repetir nueva contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    name="repetirNuevaClave"
                    value={form.repetirNuevaClave}
                    onChange={handleChange}
                    autoComplete="new-password"
                    isInvalid={!!errores.repetirNuevaClave}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errores.repetirNuevaClave}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-grid">
                  <Button type="submit" variant="success" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Actualizando...
                      </>
                    ) : (
                      "Actualizar contraseña"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default CambiarPassword;
